/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Badge,
    Image,
    Stack,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '.'
import {FormattedMessage} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import {fetchContentUrl} from '../../utils/hybrid-utils'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, ...props}) => {
    const variant = useItemVariant()
    const appConfig = getConfig().app
    const styles = useMultiStyleConfig('ItemImage')

    // We find the 'small' images in the variant's image groups based on variationValues and pick the first one
    const image = findImageGroupBy(variant?.imageGroups, {
        viewType: 'hi-res',
        selectedVariationAttributes: variant?.variationValues
    })?.images?.[0]

    return (
        <Box {...props}>
            <Stack>
                <Box {...styles.imageWrapper}>
                    {variant.c_isSale && (
                        <Badge {...styles.blueBadgeStyle}>
                            <FormattedMessage
                                defaultMessage="Sale"
                                id="item_image.label.sale"
                                description="A sale badge placed on top of a product image"
                            />
                        </Badge>
                    )}
                    {variant.isProductUnavailable && (
                        <Badge {...styles.redBadgeStyle}>
                            <FormattedMessage
                                defaultMessage="Unavailable"
                                id="item_image.label.unavailable"
                                description="A unavailable badge placed on top of a product image"
                            />
                        </Badge>
                    )}

                    {image && (
                        <Image
                            alt={image.alt}
                            src={fetchContentUrl(appConfig, image.link)}
                            ignoreFallback={true}
                            {...imageProps}
                        />
                    )}
                </Box>
            </Stack>
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object
}

export default ItemImage
